import { useAtomValue, useSetAtom } from 'jotai';

import { SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';
import { transformDisplayName } from '@ekhodealer/ekho-common/utils';

import type { FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { FiltersDrawerCheckboxGroup } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { modelFilterAtom, updateModelFilterAtom } from '../../../atoms/inventoryAtoms';
import { useInventoryFilterFacetCounts } from '../../../hooks/useInventoryFilterFacetCounts';

const LoadingCheckboxGroup = () => {
	return (
		<Stack itemsSpacing='8'>
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
		</Stack>
	);
};

/**
 * VehicleModelSection component that provides model filtering options
 * Dynamically generates options based on available models in inventory with counts
 * This is used within the VehicleInformationSection accordion
 */
export function VehicleModelSection() {
	const models = useAtomValue(modelFilterAtom);
	const setModels = useSetAtom(updateModelFilterAtom);

	// Get dynamic facet counts from the search response
	const { modelCounts, isLoading } = useInventoryFilterFacetCounts();

	// Build model items with dynamic counts from facets
	// Use transformDisplayName to style the labels
	// Sort alphabetically for consistent ordering
	const modelItems: FiltersDrawerCheckboxGroupItem[] = Object.entries(modelCounts)
		.map(([model, count]) => ({
			value: model,
			label: transformDisplayName(model),
			count,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	// Don't render if data has loaded but there are no options
	if (!isLoading && modelItems.length === 0) {
		return null;
	}

	return (
		<>
			{isLoading ? (
				<LoadingCheckboxGroup />
			) : (
				<FiltersDrawerCheckboxGroup
					label='Model'
					items={modelItems}
					value={models}
					onChange={setModels}
					tooltipHelpText='Model filter counts are based on matched inventory'
				/>
			)}
		</>
	);
}
